import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Heart, MapPin, Clock, Mail, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

const STAFF = [
  {
    name: "Pastor Demetrial Donshae Hill",
    role: "Senior Pastor",
    image: "/images/pastor-hill.jpg",
    bio: "Raised in Portsmouth and answered the call to preach on May 5, 2021. Leads with authenticity, compassion, and power — a shepherd's heart for healing, deliverance, and pushing people closer to Christ.",
  },
  {
    name: "First Lady Déja Hill",
    role: "First Lady",
    image: "/images/first-lady-hill.jpg",
    bio: "Devoted wife and mother. Chesapeake native, faithful for 12 years alongside Pastor Hill. Known for her warmth, creative gifts (hair, makeup, style, song), and a heart that makes everyone feel seen.",
  },
  {
    name: "Youth Pastor Danny Hill Jr.",
    role: "Youth Pastor",
    image: "/images/youth-pastor-danny.jpg",
    bio: "B.S. Human Services (ODU) + M.Ed. Educational Studies in Leadership & Urban Education (Liberty). Walks with the next generation — mentoring, encouraging, and making sure every young person knows they are seen and loved.",
  },
];

export default function Contact() {
  const [tab, setTab] = useState("contact");

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="contact-page">
      <Seo
        title="Contact"
        description="Reach out to The Push Center. Send a message, submit a prayer request, or plan your first visit to 6217 Portsmouth Blvd, Portsmouth, VA."
        path="/contact"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Reach Out
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Let's <span className="italic text-electric text-pulse">talk.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Got a question, need prayer, or want to plan your first visit? We're here.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6">
          <div className="p-7 rounded-2xl border border-white/5 bg-[#0a0a0a]">
            <MapPin className="w-6 h-6 text-electric mb-4" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-2">Visit</div>
            <div className="text-lg font-bold">6217 Portsmouth Blvd</div>
            <div className="text-zinc-400 mb-4">Portsmouth, VA 23701</div>
            <a
              href="https://maps.google.com/?q=6217+Portsmouth+Blvd+Portsmouth+VA+23701"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-electric font-bold text-sm hover:gap-3 transition-all"
              data-testid="contact-directions"
            >
              Get Directions →
            </a>
          </div>

          <div className="p-7 rounded-2xl border border-white/5 bg-[#0a0a0a]">
            <Clock className="w-6 h-6 text-electric mb-4" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-3">Service Times</div>
            <ul className="space-y-2 text-zinc-200 text-sm">
              <li>Sunday Worship · 9:00 AM</li>
              <li>Sunday School · 1st Sun 8:00 AM</li>
              <li>Bible Study · Wed 7:30 PM</li>
            </ul>
          </div>

          <div className="p-7 rounded-2xl border border-electric/20 bg-gradient-to-br from-electric/10 to-[#0a0a0a]">
            <Heart className="w-6 h-6 text-electric mb-4" />
            <div className="font-display font-bold text-xl mb-2">First time visiting?</div>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Come as you are. For First Sunday communion, wear all black. We can't wait to meet you.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex gap-2 mb-6 border-b border-white/5">
            <button
              onClick={() => setTab("contact")}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition-all relative ${
                tab === "contact" ? "text-electric" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="tab-contact"
            >
              <Mail className="w-4 h-4 inline mr-2" /> Contact
              {tab === "contact" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric" />}
            </button>
            <button
              onClick={() => setTab("prayer")}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition-all relative ${
                tab === "prayer" ? "text-electric" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="tab-prayer"
            >
              <Heart className="w-4 h-4 inline mr-2" /> Prayer Request
              {tab === "prayer" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric" />}
            </button>
          </div>

          {tab === "contact" ? <ContactForm /> : <PrayerForm />}
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
          Find Us
        </div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-8">
          You're <span className="italic text-electric">welcome here.</span>
        </h2>
        <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[16/9] lg:aspect-[21/8]">
          <iframe
            title="The Push Center Map"
            src="https://www.google.com/maps?q=6217+Portsmouth+Blvd+Portsmouth+VA+23701&output=embed"
            className="w-full h-full grayscale-[60%] contrast-110"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
        </div>
      </section>

      {/* STAFF DIRECTORY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-32">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
          Staff Directory
        </div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-12">
          The team you'll <span className="italic text-electric">meet.</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STAFF.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl overflow-hidden border border-white/5 bg-[#0a0a0a] group"
              data-testid={`staff-card-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={s.image}
                  alt={s.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-1">{s.role}</div>
                <div className="font-display font-bold text-xl mb-2">{s.name}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-sm text-zinc-500 mt-8 italic">
          More staff & ministry leaders coming soon.
        </p>
      </section>
    </main>
  );
}

function Field({ label, children, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2" data-testid={`label-${testid}`}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  "w-full px-4 py-3 rounded-lg bg-[#0c0c0c] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      await api.post("/contact", payload);
      toast.success("Message sent! We'll respond soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      key="contact-form"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="space-y-5"
      data-testid="contact-form"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Your Name" testid="contact-name">
          <input className={inputClass} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" data-testid="contact-input-name" />
        </Field>
        <Field label="Email" testid="contact-email">
          <input type="email" className={inputClass} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="contact-input-email" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Phone (optional)" testid="contact-phone">
          <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" data-testid="contact-input-phone" />
        </Field>
        <Field label="Subject" testid="contact-subject">
          <input className={inputClass} required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What's this about?" data-testid="contact-input-subject" />
        </Field>
      </div>
      <Field label="Message" testid="contact-message">
        <textarea rows={6} className={inputClass} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Share what's on your heart..." data-testid="contact-input-message" />
      </Field>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
        data-testid="contact-submit"
      >
        <Send className="w-4 h-4" />
        {submitting ? "Sending..." : "Send Message"}
      </button>
    </motion.form>
  );
}

function PrayerForm() {
  const [form, setForm] = useState({ name: "", email: "", request: "", is_anonymous: false });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (payload.is_anonymous) payload.name = "Anonymous";
      await api.post("/prayer-requests", payload);
      toast.success("Prayer request received. We're standing with you.");
      setForm({ name: "", email: "", request: "", is_anonymous: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      key="prayer-form"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="space-y-5"
      data-testid="prayer-form"
    >
      <div className="p-4 rounded-lg bg-electric/5 border border-electric/20 text-sm text-zinc-300">
        Your prayer request is confidential. Our intercessory team will stand with you.
      </div>
      {!form.is_anonymous && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Your Name" testid="prayer-name">
            <input className={inputClass} required={!form.is_anonymous} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" data-testid="prayer-input-name" />
          </Field>
          <Field label="Email (optional)" testid="prayer-email">
            <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="prayer-input-email" />
          </Field>
        </div>
      )}
      <Field label="Prayer Request" testid="prayer-request">
        <textarea rows={6} className={inputClass} required value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} placeholder="Share what you need prayer for..." data-testid="prayer-input-request" />
      </Field>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={form.is_anonymous}
          onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })}
          className="w-4 h-4 rounded border-white/20 bg-[#0c0c0c] text-electric accent-[#22C55E]"
          data-testid="prayer-input-anonymous"
        />
        <span className="text-sm text-zinc-300">Submit anonymously</span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
        data-testid="prayer-submit"
      >
        <Heart className="w-4 h-4" />
        {submitting ? "Submitting..." : "Submit Prayer Request"}
      </button>
    </motion.form>
  );
}
